import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    input,
    output,
    signal,
    ViewEncapsulation,
    WritableSignal,
} from "@angular/core";
import { Report } from "../../../pojo/Report";
import { S25Util } from "../../../util/s25-util";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { Merge } from "../../../pojo/Util";
import { EventService } from "../../../services/event.service";
import { Throttle } from "../../../decorators/debounce.decorator";
import { Doc } from "./s25.document.const";
import { Bind } from "../../../decorators/bind.decorator";

@Component({
    standalone: false,
    selector: "s25-ng-report-parameters",
    template: `
        @for (parameter of parameters(); track parameter) {
            <div class="parameter" [attr.data-type]="parameter.parameter_type">
                @if (parameter.hasLabel) {
                    <label>
                        {{ parameter.parameter_name }}
                        @if (parameter.required === "F") {
                            <span class="small">(optional)</span>
                        }
                    </label>
                }
                @switch (parameter.parameter_type) {
                    @case (Report.parameterType.String) {}
                    @case (Report.parameterType.Integer) {}
                    @case (Report.parameterType.Float) {}
                    @case (Report.parameterType.Time) {}
                    @case (Report.parameterType.DateTime) {}
                    @case (Report.parameterType.Object) {}
                    @case (Report.parameterType.MeetingPatterns) {}
                    @case (Report.parameterType.Date) {
                        @let datepickerModel = { date: parameter.value() };
                        <s25-datepicker
                            [modelValue]="datepickerModel"
                            (modelValueChange)="parameter.value.set(S25Util.date.toS25ISODateStr($event))"
                            [minDate]="parameter === endDtParam() ? startDt() : null"
                            [autoSelectToday]="!parameter.value()"
                        ></s25-datepicker>
                    }
                    @case (Report.parameterType.Boolean) {
                        <label>{{ parameter.parameter_name }}</label>
                        <s25-toggle-button
                            [modelValue]="parameter.value()"
                            (modelValueChange)="parameter.value.set($event ? 'T' : 'F')"
                            [trueLabel]="'Yes'"
                            [falseLabel]="'No'"
                        />
                    }
                    @case (Report.parameterType.EventSearch) {
                        <ng-container *ngTemplateOutlet="search; context: { type: 'event', parameter: parameter }" />
                    }
                    @case (Report.parameterType.OrganizationSearch) {
                        <ng-container
                            *ngTemplateOutlet="search; context: { type: 'organization', parameter: parameter }"
                        />
                    }
                    @case (Report.parameterType.LocationSearch) {
                        <ng-container *ngTemplateOutlet="search; context: { type: 'location', parameter: parameter }" />
                    }
                    @case (Report.parameterType.ResourceSearch) {
                        <ng-container *ngTemplateOutlet="search; context: { type: 'resource', parameter: parameter }" />
                    }
                    @case (Report.parameterType.GroupBy) {
                        <select class="cn-form__control" [(ngModel)]="parameter.value">
                            @for (item of parameter.options; track item.itemId) {
                                <option [ngValue]="item.itemId">{{ item.itemName }}</option>
                            }
                        </select>
                    }
                    @case ("locator") {
                        <input
                            [ngModel]="undefined"
                            (ngModelChange)="getEventIdFromLocator(parameter, $event)"
                            class="c-input event-locator"
                            placeholder="YYYY-ABCDEF"
                        />
                    }
                    @case ("occurrence") {
                        @if (!!locatorParam().value()) {
                            <label>{{ parameter.parameter_name }}</label>
                            <s25-ng-document-reservation-list
                                [eventId]="$any(locatorParam().value())"
                                (selected)="parameter.value.set($event.toString())"
                            />
                        }
                    }
                    @case ("invoice") {
                        @if (!!locatorParam().value()) {
                            <label>{{ parameter.parameter_name }}</label>
                            <s25-ng-document-invoice-list
                                [eventId]="$any(locatorParam().value())"
                                (selected)="parameter.value.set($event)"
                            />
                        }
                    }
                }
                @if (parameter.loading()) {
                    <s25-ng-loading-inline-static [text]="null" />
                }
            </div>
        }

        <ng-template #search let-type="type" let-parameter="parameter">
            <s25-ng-search-dropdown
                [type]="type"
                [allowNonQueryId]="false"
                (chosenChange)="parameter.value.set($event?.itemId)"
                [hasNoneItem]="parameter.required === 'F'"
            />
        </ng-template>
    `,
    styles: `
        :host {
            display: grid;
            grid-template-columns: auto auto 2em 1fr;
            grid-row-gap: 0.5rem;
            grid-column-gap: 1rem;
        }

        .parameter {
            display: grid;
            grid-template-columns: subgrid;
            grid-column: 1 / -1;
            align-items: center;

            &[data-type="occurrence"],
            &[data-type="invoice"] {
                grid-template-columns: 1fr;
            }
        }

        input.event-locator {
            width: 13ch;
            box-sizing: content-box;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class S25ReportParametersComponent {
    readonly rawParams = input.required<Report.Parameter[]>({ alias: "parameters" });
    readonly valuesChange = output<Record<Report.Parameter["parameter_field_mapping"], Report.ParameterValue>>({});

    readonly parameters = computed(() => this.rawParams().map(this.mapParameter));

    readonly startDt = computed(() => {
        const param = this.parameters().find((p) => /start date/i.test(p.parameter_name));
        const value = param?.value();
        return S25Util.date.isDate(value) ? value : undefined;
    });
    readonly endDtParam = computed(() => this.parameters().find((p) => /end date/i.test(p.parameter_name)));
    readonly locatorParam = computed(() => this.parameters().find((p) => p.parameter_type === "locator"));

    protected readonly Report = Report;
    protected readonly S25Util = S25Util;

    groupByMap: Record<string, string> = {
        "Reservation Start": "Occurrence Start",
        "Reservation Dates": "Occurrence Dates",
    };

    constructor() {
        effect(this.onChange);
    }

    @Bind
    mapParameter(param: Report.Parameter) {
        const newParam: Parameter = {
            ...param,
            options: [] as DropDownItem[],
            value: signal(this.defaultValue(param)),
            loading: signal(false),
            hasLabel: true,
            parameter_name: Doc.parameterNameMap[param.parameter_name] ?? param.parameter_name,
        };

        if (param.validate_type === Report.parameterValidateType.GroupBy) {
            newParam.options = param.validate
                .split(",")
                .map((val) => ({ itemId: val, itemName: this.groupByMap[val] ?? val }));
        }

        if (param.parameter_type === Report.parameterType.Boolean) {
            newParam.hasLabel = false;
        } else if (param.parameter_type === Report.parameterType.Integer && /EVENT.ID/i.test(param.parameter_name)) {
            newParam.parameter_type = "locator";
        } else if (param.parameter_type === Report.parameterType.String && /RSRV.ID/i.test(param.parameter_name)) {
            newParam.parameter_type = "occurrence";
            newParam.hasLabel = false;
        } else if (param.parameter_type === Report.parameterType.Integer && /Bill.ID/i.test(param.parameter_name)) {
            newParam.parameter_type = "invoice";
            newParam.hasLabel = false;
        }

        return newParam;
    }

    defaultValue(param: Report.Parameter): Report.ParameterValue {
        if (param.parameter_type === Report.parameterType.Date) return S25Util.date.toS25ISODateStr(new Date());
        if (param.validate_type === Report.parameterValidateType.GroupBy) return param.validate.split(",")[0];
        if (param.parameter_type === Report.parameterType.Boolean) return "F";
        return undefined;
    }

    @Bind
    onChange() {
        const newValues = Object.fromEntries(
            this.parameters().map((param) => [param.parameter_field_mapping, param.value()]),
        );
        this.valuesChange.emit(newValues);
    }

    @Throttle(500)
    async getEventIdFromLocator(parameter: Parameter, locator: string) {
        parameter.value.set(undefined);
        if (!S25Util.isEventLocator(locator)) return;
        parameter.loading.set(true);
        const [eventId, err] = await S25Util.Maybe(EventService.getEventIdByLocator(locator));
        parameter.loading.set(false);
        if (!eventId || !!err) {
            alert("Failed to load event, please make sure your event reference is correct.");
            return;
        }
        parameter.value.set(eventId);
    }
}

type Parameter = Merge<
    Report.Parameter,
    {
        options: DropDownItem[];
        value: WritableSignal<Report.ParameterValue>;
        parameter_type: Report.ParameterType | "locator" | "occurrence" | "invoice";
        loading: WritableSignal<boolean>;
        hasLabel: boolean;
    }
>;
